// src/pages/Dashboard.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Toolbar,
  Typography,
  Grid,
  Paper,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Skeleton,
  useTheme,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import CountUp from "react-countup";

import PeopleIcon from "@mui/icons-material/People";
import ShieldIcon from "@mui/icons-material/Shield";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";

import CryptoNewsPanelFrontend from "../components/CryptoNewsPanelFrontend";
import { getUsers, getKycList, getDeposits, getTransactions } from "../services/api";

/* small helpers */
const fmtNumber = (n) =>
  n === null || n === undefined || isNaN(Number(n)) ? "—" : Number(n).toLocaleString();
const fmtCurrency = (n) =>
  n === null || n === undefined || isNaN(Number(n)) ? "—" : `$${Number(n).toLocaleString()}`;

/* KPI card */
function KpiCard({ title, value, icon, color }) {
  const numeric = Number(value) || 0;
  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
        color: "#fff",
      }}
    >
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800 }}>
          <CountUp end={numeric} duration={1.2} separator="," preserveValue />
        </Typography>
      </Box>
      <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 52, height: 52 }}>{icon}</Avatar>
    </Paper>
  );
}

/* Utility: robustly extract array from axios response shapes */
function extractArray(res) {
  if (!res && !res?.data) return [];
  const r = res?.data ?? res;
  if (Array.isArray(r)) return r;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r?.result)) return r.result;
  if (Array.isArray(r?.items)) return r.items;
  for (const k of Object.keys(r || {})) {
    if (Array.isArray(r[k])) return r[k];
  }
  return [];
}

/* Utility: bucket items by month */
const getTimestamp = (item) => item?.createdAt ?? item?.date ?? item?.timestamp ?? item?.created ?? null;
function bucketByMonth(items = []) {
  const buckets = {};
  items.forEach((it) => {
    const ts = getTimestamp(it);
    let key;
    if (ts) {
      const d = new Date(ts);
      if (!isNaN(d)) key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    if (!key) key = "unknown";
    buckets[key] = (buckets[key] || 0) + 1;
  });
  return Object.entries(buckets)
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

/* ---------------------
  LivePrices component
  - uses coingecko public API by default
  - responsive layout, truncation & clamping to avoid overflow
  - replace fetch URL if you want to use your own API
----------------------*/
function LivePrices({ coins = ["bitcoin", "ethereum", "tether", "cardano"], vs = "usd", pollInterval = 30000 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  // Build coingecko-style ids string
  const ids = coins.join(",");

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;

    const fetchPrices = async () => {
      setLoading(true);
      try {
        // Example Coingecko API — replace if you have a proxy/backend
        const url = `https://api.coingecko.com/api/v3/simple/price?ids=${encodeURIComponent(
          ids
        )}&vs_currencies=${vs}&include_24hr_change=true`;
        const res = await fetch(url);
        const json = await res.json();
        if (!mountedRef.current) return;
        setData(json);
      } catch (err) {
        console.error("LivePrices fetch error", err);
        if (!mountedRef.current) return;
        setData(null);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    // initial fetch
    fetchPrices();

    // interval
    timer = setInterval(() => {
      fetchPrices();
    }, pollInterval);

    return () => {
      mountedRef.current = false;
      if (timer) clearInterval(timer);
    };
  }, [ids, vs, pollInterval]);

  const items = useMemo(() => {
    if (!data) return [];
    return coins.map((c) => {
      const d = data[c] || {};
      return {
        id: c,
        price: d[vs],
        change: d[`${vs}_24h_change`] ?? null,
      };
    });
  }, [data, coins, vs]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading live prices…</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1,
            // responsive grid: 1 column on xs, 2 on sm, N on md+
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(2, minmax(0,1fr))`,
              md: `repeat(${Math.min(items.length, 4)}, minmax(0,1fr))`,
            },
            alignItems: "stretch",
          }}
        >
          {items.map((it) => (
            <Paper
              key={it.id}
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: "transparent",
                border: "1px solid rgba(255,255,255,0.03)",
                display: "flex",
                flexDirection: "column",
                minWidth: 0, // very important for text ellipsis to work in flex containers
              }}
            >
              <Typography variant="caption" sx={{ textTransform: "capitalize", color: "text.secondary", mb: 0.5 }}>
                {it.id}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  // responsive font size clamp-like behavior using breakpoints
                  fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={it.price != null ? String(it.price) : ""}
              >
                {it.price == null ? "—" : `$${Number(it.price).toLocaleString(undefined, { maximumFractionDigits: 8 })}`}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: it.change == null ? "text.secondary" : Number(it.change) >= 0 ? "success.main" : "error.main",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={it.change != null ? `${Number(it.change).toFixed(4)}%` : ""}
              >
                {it.change == null ? "—" : `${Number(it.change).toFixed(2)}%`}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* --------------------------
  Main Dashboard component
---------------------------*/
export default function Dashboard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [kycs, setKycs] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [uRes, kRes, dRes, tRes] = await Promise.allSettled([getUsers(), getKycList(), getDeposits(), getTransactions()]);

        const uList = uRes.status === "fulfilled" ? extractArray(uRes.value) : [];
        const kList = kRes.status === "fulfilled" ? extractArray(kRes.value) : [];
        const dList = dRes.status === "fulfilled" ? extractArray(dRes.value) : [];
        const tList = tRes.status === "fulfilled" ? extractArray(tRes.value) : [];

        setUsers(uList);
        setKycs(kList);
        setDeposits(dList);
        setTransactions(tList);
      } catch (e) {
        console.error("Dashboard load error", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // derived stats
  const totalUsers = users.length || 0;
  const pendingKyc = kycs.filter((k) => (k?.status || "").toString().toLowerCase() === "pending").length || 0;
  const pendingDeposits = deposits.filter((d) => (d?.status || "").toString().toLowerCase() === "pending").length || 0;
  const totalDepositsAmount = deposits.reduce((acc, cur) => acc + (Number(cur?.amount ?? cur?.value ?? 0) || 0), 0);

  const usersChartData = bucketByMonth(users);
  const depositChartData = bucketByMonth(deposits);

  // Verified vs Non-verified pie data
  const verifiedCount = users.filter((u) => !!u?.isKycVerified || String(u?.isKycVerified || "").toLowerCase() === "true").length;
  const nonVerifiedCount = Math.max(0, totalUsers - verifiedCount);
  const verificationPie = [{ name: "Verified", value: verifiedCount }, { name: "Not Verified", value: nonVerifiedCount }];
  const verificationColors = ["#16a34a", "#f59e0b"];
  const totalForPct = verificationPie.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1;

  return (
    <Box>
      <Toolbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Admin Dashboard
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Share report">
              <IconButton>
                <ShareOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <IconButton>
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {loading ? (
          <Grid container spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {/* KPI row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard title="Total Users" value={totalUsers} icon={<PeopleIcon />} color="#2563eb" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard title="Pending KYC" value={pendingKyc} icon={<ShieldIcon />} color="#f59e0b" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard title="Pending Deposits" value={pendingDeposits} icon={<AccountBalanceWalletIcon />} color="#8b5cf6" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard title="Total Deposits" value={Number(totalDepositsAmount.toFixed(2))} icon={<AttachMoneyIcon />} color="#16a34a" />
              </Grid>
            </Grid>

            {/* Market Overview + charts */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Market Overview
                  </Typography>

                  {/* Live Prices (responsive) */}
                  <Box sx={{ mb: 2 }}>
                    <LivePrices coins={["bitcoin", "ethereum", "tether", "cardano"]} vs="usd" pollInterval={30000} />
                  </Box>

                  {/* News below (scrollable) */}
                  <Box sx={{ mt: 2, flex: 1, minHeight: 120, overflow: "auto" }}>
                    <CryptoNewsPanelFrontend max={6} />
                  </Box>
                </Paper>
              </Grid>

              {/* Charts */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        User Growth
                      </Typography>

                      <ResponsiveContainer width="100%" height={260}>
                        <ComposedChart data={usersChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                          <defs>
                            <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.9} />
                              <stop offset="95%" stopColor="#2563eb" stopOpacity={0.08} />
                            </linearGradient>
                            <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.9} />
                              <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.2} />
                            </linearGradient>
                          </defs>

                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <ReTooltip formatter={(value) => Number(value).toLocaleString()} />

                          <Area type="monotone" dataKey="count" name="Users" stroke="#2563eb" strokeWidth={2} fill="url(#colorCount)" fillOpacity={1} activeDot={{ r: 5 }} />
                          <Bar dataKey="count" name="Monthly Count" barSize={18} fill="url(#barColor)" opacity={0.6} />

                          <Legend verticalAlign="top" height={24} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Users — Verified vs Not Verified
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={verificationPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={90} paddingAngle={4} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {verificationPie.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={verificationColors[index % verificationColors.length]} />
                            ))}
                          </Pie>
                          <ReTooltip formatter={(v) => `${v} users`} />
                        </PieChart>
                      </ResponsiveContainer>

                      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
                        {verificationPie.map((p, i) => (
                          <Stack key={p.name} direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 12, height: 12, bgcolor: verificationColors[i], borderRadius: 1 }} />
                            <Typography variant="body2">
                              {p.name} — <strong>{p.value}</strong> ({((p.value / totalForPct) * 100).toFixed(0)}%)
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Recent tables */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Recent Deposits
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deposits.slice(0, 5).map((d) => (
                        <TableRow key={d.id || d._id || JSON.stringify(d).slice(0, 8)}>
                          <TableCell>{d.user?.name || d.user?.email || "—"}</TableCell>
                          <TableCell>{fmtCurrency(d.amount ?? d.value ?? d.amt)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={String(d.status ?? "PENDING")}
                              color={String(d.status) === "APPROVED" ? "success" : String(d.status) === "REJECTED" ? "error" : "warning"}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Recent KYC
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kycs.slice(0, 5).map((k) => (
                        <TableRow key={k.id || JSON.stringify(k).slice(0, 8)}>
                          <TableCell>{k.user?.name || k.user?.email || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={String(k.status ?? "PENDING")}
                              color={String(k.status) === "APPROVED" ? "success" : String(k.status) === "REJECTED" ? "error" : "warning"}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}
